import React, { useEffect, useState } from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  ReferenceInput,
  SelectInput,
  required,
  useGetList,
  useRecordContext,
  useDataProvider,
} from 'react-admin';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Divider,
  FormControl,
  InputLabel,
  Select as MUISelect,
  MenuItem,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Group as GroupIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import RolePermissionManager from '../../components/RolePermissionManager';
import { useRolePermissionEditTransform } from '../../hooks/useRolePermissionTransform';
import { useFormContext } from 'react-hook-form';

// Componente de título personalizado
const EditTitle = () => {
  const record = useRecordContext();

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
      <EditIcon color='primary' sx={{ fontSize: 32 }} />
      <Typography variant='h4' component='h1' fontWeight='bold'>
        Editar Dashboard: {record?.dashboardName || 'Cargando...'}
      </Typography>
    </Box>
  );
};

const DashboardsEdit = () => {
  // Hook para transformación de roles
  const { transformForSave } = useRolePermissionEditTransform();

  // Plant selector + filtered group selector component
  const PlantGroupSelector: React.FC = () => {
    const record = useRecordContext<any>();
    const dataProvider = useDataProvider();
    const { setValue } = useFormContext();

    const [selectedPlantId, setSelectedPlantId] = useState<number | ''>('');
    const { data: plants } = useGetList<any>('plants', {
      pagination: { page: 1, perPage: 500 },
      sort: { field: 'plantName', order: 'ASC' },
    });

    // Prefill plant based on current dashboardGroupId
    useEffect(() => {
      const loadPlantFromGroup = async () => {
        if (!record?.dashboardGroupId) return;
        try {
          const resp = await dataProvider.getOne('dashboardgroups', {
            id: Number(record.dashboardGroupId),
          });
          const pid = resp?.data?.plantId;
          if (pid) setSelectedPlantId(Number(pid));
        } catch (e) {
          // silent fail
        }
      };
      loadPlantFromGroup();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [record?.dashboardGroupId]);

    const plantOptions: any[] = Array.isArray(plants) ? plants : [];

    return (
      <>
        <FormControl size='small' fullWidth sx={{ mb: 2 }}>
          <InputLabel id='plant-select-label'>Planta</InputLabel>
          <MUISelect
            labelId='plant-select-label'
            label='Planta'
            value={selectedPlantId}
            onChange={e => {
              const v = e.target.value as number | '';
              setSelectedPlantId(v);
              // reset group when plant changes
              setValue('dashboardGroupId', null, { shouldDirty: true });
            }}
            displayEmpty
          >
            <MenuItem value=''>Seleccione una planta</MenuItem>
            {plantOptions.map((p: any) => (
              <MenuItem key={p.id} value={p.id}>
                {p.plantName}
              </MenuItem>
            ))}
          </MUISelect>
        </FormControl>

        <ReferenceInput
          source='dashboardGroupId'
          reference='dashboardgroups'
          sort={{ field: 'groupName', order: 'ASC' }}
          filter={
            selectedPlantId ? { plantId: Number(selectedPlantId) } : undefined
          }
          perPage={500}
        >
          <SelectInput
            optionText='groupName'
            optionValue='id'
            label='Grupo de Dashboard'
            validate={[required()]}
            fullWidth
            disabled={!selectedPlantId}
            helperText={
              selectedPlantId
                ? 'Seleccione el grupo asociado a la planta seleccionada'
                : 'Seleccione una planta para habilitar los grupos'
            }
          />
        </ReferenceInput>
      </>
    );
  };

  return (
    <Edit
      transform={transformForSave}
      sx={{
        '& .RaEdit-card': {
          boxShadow: 'none',
          backgroundColor: 'transparent',
        },
      }}
    >
      <Box sx={{ padding: 3, maxWidth: 1200, margin: '0 auto' }}>
        <EditTitle />

        <SimpleForm
          sx={{
            '& .RaSimpleForm-toolbar': {
              marginTop: 3,
              padding: 2,
              backgroundColor: 'background.paper',
              borderRadius: 2,
              boxShadow: 2,
            },
          }}
        >
          <Grid container spacing={3}>
            {/* Información Principal */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: 'fit-content',
                  boxShadow: 3,
                  borderRadius: 2,
                  transition: 'box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <DashboardIcon color='primary' />
                    <Typography variant='h6' fontWeight='bold'>
                      Información del Dashboard
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <TextInput
                    source='dashboardName'
                    label='Nombre del Dashboard'
                    validate={[required()]}
                    fullWidth
                    sx={{ mb: 2 }}
                    helperText='Nombre descriptivo para el dashboard'
                  />
                </CardContent>
              </Card>
            </Grid>

            {/* Grupo y Configuración */}
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  height: 'fit-content',
                  boxShadow: 3,
                  borderRadius: 2,
                  transition: 'box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <GroupIcon color='primary' />
                    <Typography variant='h6' fontWeight='bold'>
                      Asignación
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <PlantGroupSelector />
                </CardContent>
              </Card>
            </Grid>

            {/* Gestión de Roles */}
            <Grid item xs={12}>
              <Card
                sx={{
                  boxShadow: 3,
                  borderRadius: 2,
                  transition: 'box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    boxShadow: 6,
                  },
                }}
              >
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: 2,
                    }}
                  >
                    <GroupIcon color='primary' />
                    <Typography variant='h6' fontWeight='bold'>
                      Gestión de Roles
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <RolePermissionManager
                    title='Permisos del Dashboard'
                    subtitle='Modificar roles y permisos para este dashboard'
                    helperText='Los usuarios con diferentes roles tendrán diferentes niveles de acceso a este dashboard y sus funcionalidades.'
                    showCard={false}
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </SimpleForm>
      </Box>
    </Edit>
  );
};

export default DashboardsEdit;
